import React, { forwardRef, type ReactElement } from 'react';
import { List, type RowComponentProps } from 'react-window';

const LISTBOX_PADDING = 2; // px
const ITEM_SIZE = 36; // px

export type AutocompleteSelectorListboxProps = React.HTMLAttributes<HTMLElement> & {
  dropdownHeight?: string;
};

type SelectorListboxRowProps = {
  itemNodes: React.ReactElement<Record<string, unknown>>[];
};

function SelectorListboxRow({ index, style, itemNodes }: RowComponentProps<SelectorListboxRowProps>): ReactElement {
  const child = itemNodes[index];
  const match = typeof style.transform === 'string' ? style.transform.match(/translateY\((-?[\d.]+)px\)/) : null;
  const currentY = match ? Number(match[1]) : 0;

  return React.cloneElement(child, {
    style: {
      ...style,
      transform: `translateY(${currentY + LISTBOX_PADDING}px)`,
    },
    ...child.props,
  });
}

export const AutocompleteSelectorListbox = forwardRef<HTMLDivElement, AutocompleteSelectorListboxProps>(
  function VirtualListboxComponent({ children, dropdownHeight, className, style, ...other }, ref) {
    const itemNodes = React.Children.toArray(children) as React.ReactElement<Record<string, unknown>>[];
    const itemCount = itemNodes.length;

    const calculatedHeight = dropdownHeight
      ? parseInt(dropdownHeight, 10)
      : Math.min(8, itemCount) * ITEM_SIZE + 2 * LISTBOX_PADDING;

    return (
      <div ref={ref} className={className} style={style} {...other}>
        <List
          rowComponent={SelectorListboxRow}
          rowCount={itemCount}
          rowHeight={ITEM_SIZE}
          rowProps={{ itemNodes }}
          overscanCount={5}
          style={{ height: calculatedHeight, width: '100%' }}
        />
      </div>
    );
  }
);
